import { useEffect, useState } from "react";
import pinIcon from "@icons/round-pin.svg";
import roundPinIcon from "@icons/pin-red.svg";
import { GoogleMap, InfoWindow, Marker } from "@react-google-maps/api";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import Button from "@hudson/ui/components/FormFields/Button";

function LocationMap({
  currentLocation,
  hoverLoc,
  locations = [],
  selectLocation,
  width,
  height,
}: {
  currentLocation?: be.Location;
  hoverLoc?: be.Location;
  locations: be.Location[];
  selectLocation: (loc: be.Location) => void;
  width: string;
  height: string;
}): JSX.Element | null {
  const [map, setMap] = useState<google.maps.Map | null>();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const { loading, data, error } = useConfig(["defaultSearchCenter { lat lng }"]);
  const {
    data: cData,
    loading: cLoading,
    error: cError,
  } = useContent(["beNearbyLocsAway", "beNearbyLocsFeet", "beNearbyLocsMiles"]);

  // Set bounds.
  useEffect(() => {
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    locations.forEach((loc) => bounds.extend(loc.latLng));
    if (currentLocation) bounds.extend(currentLocation.latLng);
    map.fitBounds(bounds);
    map.setCenter(bounds.getCenter());
  }, [map, currentLocation, locations]);

  if (error) console.error(error);

  function niceDistance(distanceinKm: number): string {
    const distance = distanceinKm * 0.6213711922;
    const distanceInFt = distance * 5280;

    if (distanceInFt < 1000)
      return `${Math.ceil(distanceInFt / 10) * 10} ${cData?.content.beNearbyLocsFeet}`;
    return `${distance.toFixed(1)} ${cData?.content.beNearbyLocsMiles}`;
  }

  return error || cError || loading || cLoading ? null : (
    <GoogleMap
      mapContainerStyle={{ width, height }}
      center={currentLocation?.latLng || data?.config.defaultSearchCenter}
      onClick={() => setActiveKey(null)}
      onLoad={setMap}
      options={{
        clickableIcons: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: "administrative",
            elementType: "all",
            stylers: [
              {
                visibility: "simplified",
              },
              { weight: 0.6 },
              {
                saturation: -100,
              },
              {
                lightness: 21,
              },
            ],
          },
          {
            featureType: "landscape",
            elementType: "all",
            stylers: [
              {
                visibility: "simplified",
              },
              {
                // hue: "#0066ff",
              },
              {
                // saturation: 100,
              },
              {
                // lightness: 25,
              },
            ],
          },
          {
            featureType: "poi",
            elementType: "all",
            stylers: [
              {
                visibility: "simplified",
              },
              { weight: 0.1 },
              {
                saturation: -100,
              },
              {
                lightness: 31,
              },
            ],
          },
          {
            featureType: "road",
            elementType: "all",
            stylers: [
              {
                visibility: "simplified",
              },
            ],
          },
          {
            featureType: "road.highway",
            elementType: "all",
            stylers: [
              {
                visibility: "on",
              },
              {
                weight: 0.6,
              },
              {
                // saturation: -85,
              },
              {
                lightness: 61,
              },
            ],
          },
          {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [
              {
                visibility: "on",
              },
            ],
          },
          {
            featureType: "road.arterial",
            elementType: "all",
            stylers: [
              {
                visibility: "on",
              },
            ],
          },
          {
            featureType: "road.local",
            elementType: "all",
            stylers: [
              {
                visibility: "on",
              },
            ],
          },
          {
            featureType: "transit",
            elementType: "all",
            stylers: [
              {
                visibility: "simplified",
              },
            ],
          },
          {
            featureType: "water",
            elementType: "all",
            stylers: [
              {
                visibility: "simplified",
              },
              {
                color: "#5f94ff",
              },
              {
                lightness: 26,
              },
              {
                saturation: -30,
                // gamma: 5.86,
              },
            ],
          },
        ],
      }}
    >
      {currentLocation && (
        <Marker
          key="current"
          title={currentLocation.displayName}
          position={currentLocation.latLng}
          icon={{ scaledSize: new google.maps.Size(35, 35), url: pinIcon.src }}
        />
      )}
      {locations.map((loc, i) => (
        <Marker
          key={loc.code}
          title={loc.name}
          label={{
            text: `${i + 1}`,
            className: "relative -top-2",
            color: "white",
          }}
          onClick={() => {
            if (activeKey != loc.code) setActiveKey(loc.code);
          }}
          position={loc.latLng}
          icon={{
            scaledSize: new google.maps.Size(hoverLoc == loc ? 50 : 35, hoverLoc == loc ? 50 : 35),
            url: roundPinIcon.src,
          }}
        >
          {activeKey == loc.code && (
            <InfoWindow onLoad={() => document.activeElement?.blur()}>
              <div
                key={loc.code}
                className="break-inside-avoid-column "
                onClick={() => {
                  // onHide();
                  // onLocationSelect(loc);
                }}
              >
                <div className="ml-2">
                  <div
                    style={{ color: "var(--primary-color)" }}
                    className="flex items-center m-0 text-md sm:text-sm "
                  >
                    <span className="text-base">{loc.displayName}</span>
                  </div>
                  <p className="break-before-avoid font-thin text-gray-400 text-sm m-0 mt-1">
                    {loc.displayAddress}
                  </p>
                  {loc.city && (
                    <p className="break-before-avoid font-thin text-gray-400 text-sm m-0">
                      {loc.city}
                      {loc.state && `, ${loc.state}`}
                    </p>
                  )}

                  {loc.distance && loc.distance > 0 ? (
                    <p style={{ color: "black" }} className="m-0 text-md sm:text-sm mt-2 ">
                      {niceDistance(loc.distance)} {cData?.content.beNearbyLocsAway}
                    </p>
                  ) : null}
                </div>

                <Button
                  onClick={() => selectLocation(loc)}
                  className="mt-4 ml-2 mb-2 hover:underline"
                >
                  Select
                </Button>
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}
    </GoogleMap>
  );
}

export default LocationMap;
